// This file contains functions for calling web services which deliver data required by listing page
// Majority of these functions should never finished with error or throw a error
// They should always return a status object with following format:
/*
{
  ok: false || true, // if gathering data finished successfully, required
  customErrorMessage: `some message`, // a description of error, required
  errorObject: errorObject, // error object e. g. from try/catch
  errorCode: 502 // HTTP error code
}
*/
import 'isomorphic-fetch';
import { stringify } from 'query-string';
import urljoin from 'url-join';

import { resolveByCountry, getCookie, setCookie } from '@codex/helpers';

import * as log from 'loglevel';
import Configuration from '../../../configuration';
import UISettings from '../settings/UISettings';
import { cdnUrl } from '../../../utils/url/url';
import groupcallMockData from '../../../services/mocks/groupcall.mock.data';
import { fetch } from '../../../services/fetch/fetch';
import { convertUCMSHierarchyToReduxFormat } from '../dataConverters/ucmsHierarchyConverter';


// Uncomment following code for testing hooklogic on dev using mock data
// It's commented in order to decrease production bundle
// import hooklogicTelevisionsMockData from '../dataConverters/mockData/hooklogicTelevisions.mock';
// import hooklogicLaptopsMockData from '../dataConverters/mockData/hooklogicLaptops.mock';


const ADD_TO_BASKET_TIMEOUT = 60000;

const urlSortingToSearchLabsSortingMap = {
  'relevance-desc': 'relevance',
  'brand-asc': 'brand_label',
  'brand-desc': 'brand_label_desc',
  'price-asc': 'price',
  'price-desc': 'price_desc',
  'rating-desc': 'average_score_desc',
  'discount-desc': 'discount_desc'
};


const PRODUCT_AVAILABILITIES_CHECK_URL = Configuration.get('api.storemode.product.deliverabilityMulti');
const PRODUCT_AVAILABILITIES_CHECK_URL_NO_POSTCODE = Configuration.get('api.storemode.product.deliverabilityMultiNoPostcode');

const CUSTOMER_SEGMENT_CONFIG_PREFIX = 'config_';

const SEARCHLABS_DOMAIN_CDN_PART = 'cdn.';


export async function fetchUCMSHierarchy() {
  let ucmsResponse;

  try {
    ucmsResponse = await fetch(Configuration.get('api.listingPage.ucmsHierarchy'));
    if (!ucmsResponse.ok) {
      return {
        ok: false,
        customErrorMessage: `Fetching UCMS hierarchy from Fo-Site from URL ${Configuration.get('api.listingPage.ucmsHierarchy')} 
            finished with HTTP error code: ${ucmsResponse.status}<br>  And the error message: ${ucmsResponse.statusText} <br><br>
            Check if Fo-site API working correctly`,
        errorCode: 500
      };
    }

    const ucmsJSON = await ucmsResponse.json();
    const convertedUCMSHierarchy = convertUCMSHierarchyToReduxFormat(ucmsJSON);

    return {
      ok: true,
      ucmsHierarchy: convertedUCMSHierarchy
    };

  } catch (err) {
    return {
      ok: false,
      errorObject: err,
      customErrorMessage: `An error occurred during fetching UCMS hierarchy from Fo-Site from URL 
        ${Configuration.get('api.listingPage.ucmsHierarchy')} Or the 
        response is not in the correct format and it's not possible to parse it to a proper JSON object. Or
        it wasn't possible to convert UCMS hierarchy to redux format.
        Check the Fo-site API URL or the returned data in the response`,
      errorCode: 500
    };
  }
}

// fetches data for products shown on listing page
// it calls following Searchlabs endpoint at first:  /navigation
// if searchLabs endpoint response contains non-empty 'general.redirect' property,
// the property is returned and no SearchAPI is called.
// then it calls Search API on following endpoint: /smartphone/api/products/[productIds]
export async function fetchProductsListingPageData(searchLabsRequestAttrs = {}, useFallbackSearchLabsURL) {
  const [customerSegment, config] = getURLParametersForSearchLabsABTest();

  const searchLabsRequestParams = {
    do: 'json',
    sp_cs: 'UTF-8',
    page: searchLabsRequestAttrs.page ? searchLabsRequestAttrs.page : 1,
    sort: searchLabsRequestAttrs.sort ? urlSortingToSearchLabsSortingMap[searchLabsRequestAttrs.sort] : 'relevance',
    m_results_per_page: searchLabsRequestAttrs.m_results_per_page ? searchLabsRequestAttrs.m_results_per_page : 20,
    ...(UISettings.get('search.api.load.empty.filters') === true && { return_empty_facets: true }),
    ...(searchLabsRequestAttrs.query && { q: searchLabsRequestAttrs.query }),
    ...(searchLabsRequestAttrs.price_max && { price_max: searchLabsRequestAttrs.price_max }),
    ...(searchLabsRequestAttrs.price_min && { price_min: searchLabsRequestAttrs.price_min }),
    ...(customerSegment && { customer_segment: customerSegment }),
    ...(config && { config: config }),
    ...encodeSearchFilterAttributes(searchLabsRequestAttrs.filterAttributes || {})
  };

  let fetchOptions;

  // eslint-disable-next-line camelcase
  if (typeof window === 'undefined' && process?.env?.http_proxy) {
    fetchOptions = createFetchOptionsWithHttpProxy();
  }

  let searchLabsAPIData;
  let searchLabsAPIURL;
  let storeAPIURL;

  // Creating correct URL to searchLabs
  try {
    searchLabsAPIURL = `${Configuration.get('api.listingPage.searchAndFilter')}?${stringify(searchLabsRequestParams)}`;
  } catch (err) {
    return {
      ok: false,
      errorObject: err,
      customErrorMessage: `It wasn't possible to create correct URL for searchLabsAPI call. The searchLabs API endpoint is 
        ${Configuration.get('api.listingPage.searchAndFilter')}.<br> The current request parameters are <br><br>
        ${Object.entries(searchLabsRequestParams).map((entry) => `${entry.key}: ${entry.value} <br>`)} <br><br>
        Check the endpoint and the parameters`,
      errorCode: 500
    };
  }
  searchLabsAPIURL = fallbackSearchLabsURLIfNeeded(searchLabsAPIURL, useFallbackSearchLabsURL);

  // fetching data from SearchLabs
  try {
    let searchLabsAPIResponse = await fetch(searchLabsAPIURL, fetchOptions ? fetchOptions : {});
    if (!searchLabsAPIResponse.ok && searchLabsAPIURL.includes(SEARCHLABS_DOMAIN_CDN_PART)) {

      // try fallback URL if the original one is the CDN one
      searchLabsAPIURL = removeSearchLabsCDNDomainPart(searchLabsAPIURL);
      setCookie('search_fallback_url', 'true', 1);
      console.error(`Trying to query searchLabsAPI with fallback URL ${searchLabsAPIURL}`);
      searchLabsAPIResponse = await fetch(searchLabsAPIURL, fetchOptions ? fetchOptions : {});
      if (!searchLabsAPIResponse.ok && searchLabsAPIURL.contains(SEARCHLABS_DOMAIN_CDN_PART)) {
        searchLabsAPIURL = removeSearchLabsCDNDomainPart(searchLabsAPIURL);
        searchLabsAPIResponse = await fetch(searchLabsAPIURL, fetchOptions ? fetchOptions : {});
        if (!searchLabsAPIResponse.ok) {
          return {
            ok: false,
            customErrorMessage: `Fetching data from SearchLabs from URL ${searchLabsAPIURL} finished with HTTP error code: 
            ${searchLabsAPIResponse.status}<br>  And the error message: ${searchLabsAPIResponse.statusText} <br><br>
            Check if SearchLabs API working correctly`,
            errorCode: 500
          };
        }
      }
    }
    searchLabsAPIData = await searchLabsAPIResponse.json();

    // boolean values for pages and filters values in SearchLabs response are strings ( e. g. 'true', 'false')
    // so we convert them to real boolean using this function
    searchLabsAPIData = convertStringBooleanValuesToRealBooleanInSearchLabsAPIData(searchLabsAPIData);

  } catch (err) {
    return {
      ok: false,
      errorObject: err,
      customErrorMessage: `An error occurred during fetching data from SearchLabs from URL ${searchLabsAPIURL} <br> Or the 
      response is not in the correct format and it's not possible to parse it to a proper JSON object.<br>
      Check the SearchLabs API URL or the returned data in the response`,
      errorCode: 500
    };
  }

  // if SearchLabs API is called with query parameter and response contains non-empty 'general.redirect' property,
  // it means that user of the SearchLabs API should redirect to the URL from 'general.redirect' property.
  // That's why we return just redirect URL and we don't continue with calling Store API
  // We don't do redirect if redirect URL page is a product page and in this case we proceed normally
  // The last condition checks if the redirect URL page is a product page
  // because all product page URLs contains '-pdt.html' string
  if (searchLabsRequestAttrs.query && searchLabsAPIData?.general?.redirect &&
    !searchLabsAPIData.general.redirect.includes('-pdt.html')) {

    // This code changes redirect URL on our test environments
    // It adds '/beta' before '/gbuk' if redirect URL is a listing page
    // so all searches redirect will be redirect to new listing page
    // TODO: remove this as soon as the new listing is on the production
    const redirectUrl =
      (Configuration.get('environment')?.includes('test') && searchLabsAPIData?.general?.redirect?.includes('xx-criteria.html'))
        ? searchLabsAPIData.general.redirect.replace('%2Fgbuk%2F', '%2Fbeta%2Fgbuk%2F')
        : searchLabsAPIData.general.redirect;


    return {
      ok: true,
      redirectUrl: redirectUrl,
      httpStatusCode: 302,
      usingFallbackSearchLabsURL: isFallbackSearchLabsURL(searchLabsAPIURL)
    };
  }


  const productIds = getProductIdsFromSearchLabsResponse(searchLabsAPIData);

  let storeAPIData;
  if (searchLabsAPIData.general.total !== '0') {
    storeAPIURL = `${Configuration.get('api.listingPage.products')}/${productIds.join(',')}`;

    // Fetching data from Store API
    try {
      const storeAPIResponse = await fetch(storeAPIURL, fetchOptions ? fetchOptions : {});

      if (!storeAPIResponse.ok) {
        return {
          ok: false,
          customErrorMessage: `Fetching data from Store API from URL ${storeAPIURL} finished with HTTP error code: 
           ${storeAPIResponse.status}<br>  And the error message: ${storeAPIResponse.statusText} <br><br>
           Check if Store API working correctly`,
          errorCode: 500
        };
      }
      storeAPIData = await storeAPIResponse.json();

      if (storeAPIData.error && typeof storeAPIData.error === 'object') {
        return {
          ok: false,
          customErrorMessage: `An error occurred during fetching data from Store API from URL ${storeAPIURL} The Store API 
          returned error with code: ${storeAPIData.error.code} and message: ${storeAPIData.error.message}`,
          errorCode: 500
        };
      }

    } catch (err) {
      return {
        ok: false,
        errorObject: err,
        customErrorMessage: `An error occurred during fetching data from Store API from URL ${storeAPIURL} <br> Or the 
          response is not in the correct format and it's not possible to parse it to a proper JSON object.<br>
          Check the Store API URL or the returned data in the response`,
        errorCode: 500
      };
    }

  } else {
    // if searchLabs return no products, it's not necessary to call Store API
    storeAPIData = { payload: [] };
  }


  if (searchLabsAPIData?.general?.total !== '0' && storeAPIData?.payload?.length === 0) {
    return {
      ok: false,
      customErrorMessage: `The response from Store API ${storeAPIURL} contains no data for any product but the response from 
        SearchLabs ${searchLabsAPIURL}, which was gathered before the Store API call, contains the data for products.<br>
        It means that there is an inconsistency between SearchLabs and Store API<br><br>
        Check the both calls`,
      errorCode: 500
    };
  }

  return {
    ok: true,
    data: {
      searchLabsAPIData: searchLabsAPIData,
      productsDataFromStoreAPI: storeAPIData.payload,
      productIds: productIds
    },
    usingFallbackSearchLabsURL: isFallbackSearchLabsURL(searchLabsAPIURL)
  };
}

function encodeSearchFilterAttributes(normalizedParams = {}) {
  const encodedParams = {};
  Object.keys(normalizedParams).forEach((attributeId, index) => {
    if (attributeId === 'tag') {
      encodedParams[`x${index + 1}`] = `${attributeId}`;
      // tag can contain '_' characters which have to be changed to whitespace '%20' char for search API URL request
      encodedParams[`q${index + 1}`] = `${normalizedParams[attributeId].replace(/_/g, '%20')}`;
    } else if (attributeId === 'id' || attributeId === 'externalref') {
      encodedParams[`x${index + 1}`] = `${attributeId}`;
      // the separator has mistakes it should be '|' but sometimes it's '%257c' in URLs
      encodedParams[`q${index + 1}`] = `${normalizedParams[attributeId].replace(/%(25)?7c/g, '|')}`;
    } else if (typeof normalizedParams[attributeId] !== 'undefined') {
      encodedParams[`x${index + 1}`] = `${attributeId}`;
      encodedParams[`q${index + 1}`] = `${normalizedParams[attributeId].join('|')}`;
    }
  });
  return encodedParams;
}

function getProductIdsFromSearchLabsResponse(searchLabsAPIData = {}) {
  if (
    searchLabsAPIData.resultsets &&
    searchLabsAPIData.resultsets.default &&
    searchLabsAPIData.resultsets.default.results &&
    Array.isArray(searchLabsAPIData.resultsets.default.results)
  ) {
    return searchLabsAPIData.resultsets.default.results.map((product) => product.id);
  }

  return [];
}

function isFallbackSearchLabsURL(searchLabsURL) {
  return !searchLabsURL.includes(SEARCHLABS_DOMAIN_CDN_PART);
}

function fallbackSearchLabsURLIfNeeded(searchLabsURL, useFallbackSearchLabsURLFromRequest) {
  const usingFallbackSearchLabsURLFromCookie = getCookie('search_fallback_url');
  if (usingFallbackSearchLabsURLFromCookie || useFallbackSearchLabsURLFromRequest) {
    searchLabsURL = removeSearchLabsCDNDomainPart(searchLabsURL);
  }
  return searchLabsURL;
}

function removeSearchLabsCDNDomainPart(url) {
  return url.replace(SEARCHLABS_DOMAIN_CDN_PART, '');
}

// fetches products from SearchLabs based on Ids of products
// it's used e. g. for gathering data for products shown in Compare Popup
export async function fetchProductsDataFromSearchLabsByProductIds(productIds = []) {
  let fetchOptions;

  // eslint-disable-next-line camelcase
  if (typeof window === 'undefined' && process?.env?.http_proxy) {
    fetchOptions = createFetchOptionsWithHttpProxy();
  }

  let searchLabsURL =
    `${Configuration.get('api.listingPage.searchAndFilter')}?do=json&x1=id&q1=${productIds.join('|')}`;
  searchLabsURL = fallbackSearchLabsURLIfNeeded(searchLabsURL, false);

  let searchLabsResponse;
  try {

    searchLabsResponse = await fetch(searchLabsURL, fetchOptions ? fetchOptions : {});

    if ((searchLabsResponse.ok === false) && searchLabsURL.contains(SEARCHLABS_DOMAIN_CDN_PART)) {
      searchLabsURL = removeSearchLabsCDNDomainPart(searchLabsURL);
      console.info(`Trying to query searchLabsAPI with fallback URL ${searchLabsURL}`);
      searchLabsResponse = await fetch(searchLabsURL, fetchOptions ? fetchOptions : {});
      if (!searchLabsResponse.ok) {
        return {
          ok: false,
          customErrorMessage: `Fetching data from Search Labs URL 
          ${searchLabsURL} finished with HTTP error code: ${searchLabsURL.status}<br> 
          And the error message: ${searchLabsURL.statusText} <br><br>
          Check if Search Labs API working correctly`,
          errorCode: 500
        };
      }
    }

    const searchLabsResponseData = await searchLabsResponse.json();

    return {
      ok: true,
      searchLabsResponseData: { ...searchLabsResponseData }
    };
  } catch (err) {
    return {
      ok: false,
      errorObject: err,
      customErrorMessage: `An error occurred during fetching data from Search Labs from URL 
      ${searchLabsURL} <br>
      Or the response is not in the correct format and it's not possible to parse it to a proper JSON object.<br>
      Check the SearchLabs or the returned data in the response`,
      errorCode: 500
    };
  }
}

// fetches products data with technical specifications from Store API
// it calls Store API endpoint /smartphone/api/products/[productIds]/technicalSpecifications
// it's used e. g. for gathering data for products shown in Compare Popup
export async function fetchProductsDataWithTechnicalSpecifications(productIds, requestStartTime) {
  let fetchOptions;

  // eslint-disable-next-line camelcase
  if (typeof window === 'undefined' && process?.env?.http_proxy) {
    fetchOptions = createFetchOptionsWithHttpProxy();
  }

  const storeAPIURLProductsWithTechnicalSpecifications =
    `${Configuration.get('api.listingPage.products')}/${productIds.join(',')}/technicalSpecifications`;

  let storeAPIResponse;
  try {

    storeAPIResponse = await fetch(storeAPIURLProductsWithTechnicalSpecifications, fetchOptions ? fetchOptions : {});

    if (!storeAPIResponse.ok) {
      return {
        ok: false,
        customErrorMessage: `Fetching data from Store API technical specifications endpoint from URL 
        ${storeAPIURLProductsWithTechnicalSpecifications} finished with HTTP error code: ${storeAPIResponse.status}<br> 
        And the error message: ${storeAPIResponse.statusText} <br><br>
        Check if Store API working correctly`,
        requestStartTime,
        errorCode: 500
      };
    }

    const storeAPIResponseData = await storeAPIResponse.json();

    if (storeAPIResponseData.error && typeof storeAPIResponseData.error === 'object') {
      return {
        ok: false,
        customErrorMessage: `An error occurred during fetching data from Store API from URL ${storeAPIResponseData} The Store API 
          returned error with code: ${storeAPIResponseData.error.code} and message: ${storeAPIResponseData.error.message}`,
        errorCode: 500
      };
    }

    return {
      ok: true,
      products: storeAPIResponseData.payload,
      requestStartTime
    };
  } catch (err) {
    return {
      ok: false,
      errorObject: err,
      customErrorMessage: `An error occurred during fetching data from Store API technical specifications endpoint from URL 
      ${storeAPIURLProductsWithTechnicalSpecifications} <br>
      Or the response is not in the correct format and it's not possible to parse it to a proper JSON object.<br>
      Check the Store API URL or the returned data in the response`,
      requestStartTime,
      errorCode: 500
    };
  }
}

// fetches products' stock availability from Store API
// it calls Store API endpoint /smartphone/api/productsStock/[productIds]
// it's used e. g. for showing default channels availibility for products on listing page
// (if product is on stock or if it can be collected in a store)
export async function fetchProductsStockAvailability(productIds) {
  let fetchOptions;

  // eslint-disable-next-line camelcase
  if (typeof window === 'undefined' && process?.env?.http_proxy) {
    fetchOptions = createFetchOptionsWithHttpProxy();
  }

  const storeStockAPIURL =
      `${Configuration.get('api.listingPage.productsStock')}/${productIds.join(',')}`;

  let storeStockAPIResponse;
  try {

    storeStockAPIResponse = await fetch(storeStockAPIURL, fetchOptions ? fetchOptions : {});
    const storeStockAPIData = await storeStockAPIResponse.json();

    if (!storeStockAPIResponse.ok) {
      return {
        ok: false,
        customErrorMessage: `Fetching data from Store API stock endpoint from URL ${storeStockAPIURL} finished with HTTP error 
        code: ${storeStockAPIResponse.status}<br>  And the error message: ${storeStockAPIResponse.statusText} <br><br>
        Check if Store API working correctly`,
        errorCode: 500
      };
    }

    return {
      ok: true,
      storeStockAPIData
    };
  } catch (err) {
    return {
      ok: false,
      errorObject: err,
      customErrorMessage: `An error occurred during fetching data from Store API stock endpoint from URL ${storeStockAPIURL} <br>
      Or the response is not in the correct format and it's not possible to parse it to a proper JSON object.<br>
      Check the Store API URL or the returned data in the response`,
      errorCode: 500
    };
  }
}

export async function fetchNavigationData() {
  const navigationDataURL = Configuration.get('api.navigation');

  log.info('[GET] configuration.getNavigationData', navigationDataURL);

  try {
    const navigationDataResponse = await fetch(
      navigationDataURL,
      {
        credentials: 'omit'
      }
    );

    if (navigationDataResponse.status !== 200) {
      return {
        ok: false,
        customErrorMessage: `The navigation API response gathered from URL ${navigationDataURL} has error status. 
        The status code is ${navigationDataResponse.status}. The status text is ${navigationDataResponse.statusText}`,
        errorCode: 500
      };
    }

    const navigationData = await navigationDataResponse.json();

    return {
      ok: true,
      data: navigationData
    };
  } catch (err) {

    return {
      ok: false,
      errorObject: err,
      customErrorMessage: `An error occurred during fetching navigation Data from Store API 
      navigation endpoint from URL ${navigationDataURL} Or the response is not in the correct format and 
      it's not possible to parse it to a proper JSON object Check the Store API URL or the returned data in the response`,
      errorCode: 500
    };
  }
}

export async function fetchGroupcallData() {
  if (Configuration.get('listingPage.useGroupcallMockData')) {
    // mock data for groupcall are used
    log.warn('[GROUPCALL]: Used following mocked data for groupcall:');
    log.warn(groupcallMockData);
    return Promise.resolve(groupcallMockData);
  }

  const url = Configuration.get('api.groupcall.listingPage');

  log.info('[GET] configuration.groupcall', url);

  try {
    const groupcallResponse = await fetch(
      url,
      { method: 'GET', credentials: 'include' }
    );

    if (groupcallResponse.status !== 200) {
      log.warn('[GROUPCALL FAILED]: Groupcall finished with following error code and status');
      log.warn(`Status: ${groupcallResponse.status} Status text: ${groupcallResponse.statusText}`);
      return {};
    }

    log.warn('[GROUPCALL]: Finished succesfully');
    return groupcallResponse.json();

  } catch (e) {
    log.error('[GROUPCALL CALL FAILED]: Error message');
    log.error(e.message);
  }

  return {};
}

// fetches sponsored products data from Store API based on segment
// it calls Store API endpoint .store/api/hooklogicProducts/?ucms={segmentId}
// it's used e. g. for showing sponsored products on the listing page
export async function fetchSponsoredProductsForSegment(segmentId) {
  const queryParams = `?ucms=${segmentId}`;
  const hooklogicApiUrl = `${Configuration.get('api.listingPage.sponsoredProducts')}/${queryParams}`;

  let hooklogicFetchResult;
  try {
    hooklogicFetchResult = await fetch(hooklogicApiUrl, {
      method: 'GET',
      credentials: 'include'
    });

    if (!hooklogicFetchResult.ok) {
      return {
        ok: false,
        customErrorMessage: `Fetching data from Hooklogic from URL ${hooklogicApiUrl} finished with HTTP error code: 
        ${hooklogicFetchResult.status}<br>  And the error message: ${hooklogicFetchResult.statusText} <br><br>
        Check if SearchLabs API working correctly`,
        errorCode: 500
      };
    }
    const jsonResponse = await hooklogicFetchResult.json();

    // Uncomment following code for testing hooklogic on dev using mock data on televison and laptops segment
    // It's commented in order to decrease production bundle
    /*    if (Array.isArray(jsonResponse.payload.items) && jsonResponse.payload.items.length === 0 &&
      (hooklogicApiUrl.includes('.dev.dixons.com') || hooklogicApiUrl.includes('http://store-api.dx.localhost'))) {

      if (segmentId === '30002') {
        jsonResponse.payload = hooklogicTelevisionsMockData;
      }

      if (segmentId === '30328') {
        jsonResponse.payload = hooklogicLaptopsMockData;
      }

    } */

    return {
      ok: true,
      data: jsonResponse
    };

  } catch (err) {
    return {
      ok: false,
      errorObject: err,
      customErrorMessage: `An error occurred during fetching data from Hooklogic from URL ${hooklogicApiUrl} <br> Or the 
      response is not in the correct format and it's not possible to parse it to a proper JSON object.<br>
      Check the Hooklogic API URL or the returned data in the response`,
      errorCode: 500
    };
  }
}

// Gathers data from dynamic UBO service (settings, transltions and marketing HTML content )
export async function fetchFromUBODynamicContentService(params = {}) {
  let fetchOptions;

  // eslint-disable-next-line camelcase
  if (typeof window === 'undefined' && process?.env?.http_proxy) {
    fetchOptions = createFetchOptionsWithHttpProxy();
  }

  let queryParameter = '';
  Object.keys(params).forEach((key, index) => {
    queryParameter += `${key}=${params[key]}${index !== Object.keys(params).length - 1 ? '&' : ''}`;
  });

  try {
    const uboDataFetchResult =
      await fetch(
        urljoin(Configuration.get('api.content'), `/dynamic-content?${queryParameter}`),
        fetchOptions ? fetchOptions : {});

    const uboDataJSONObject = await uboDataFetchResult.json();

    if (!uboDataFetchResult.ok) {
      return {
        ok: false,
        customErrorMessage: 'No record found for specified parameters in URL ' +
          `${urljoin(Configuration.get('api.content'), `/dynamic-content?${queryParameter}`)} in UBO dynamic content. ` +
          'You have to define record with these parameters in UBO site management.',
        errorCode: 500
      };
    }

    if (uboDataJSONObject?.content?.props?.children?.length === 1 &&
      uboDataJSONObject.content.props.children[0]?.type === 'JSONBlock' &&
      typeof uboDataJSONObject.content.props.children[0]?.props?.contentJson === 'string') {

      // if UBO response contains only one JSONBlock, it means that the data are some settings in JSON format
      // so we parse the JSON from JSONBlock and we return parsed JSON
      const parsedJSONData = JSON.parse(uboDataJSONObject.content.props.children[0].props.contentJson);
      return {
        ok: true,
        data: parsedJSONData
      };
    }

    // in all other cases we return the whole JSON object
    return {
      ok: true,
      data: uboDataJSONObject
    };
  } catch (err) {
    return {
      ok: false,
      customErrorMessage: `Error during fetching or parsing data from UBO from URL ${
        urljoin(Configuration.get('api.content'), `/dynamic-content?${queryParameter}`)
      }\nError message:  ${err.message}` +
        `\nError stack: ${err.stack}`,
      errorCode: 500
    };
  }
}

// Gathers fallback data from fo-app CDN, when a UBO call fails
export async function fetchFallbackStaticData(url) {
  try {
    const fallbackStaticDataResponse = await fetch(cdnUrl(url));
    const fallbackStaticDataResponseJSON = await fallbackStaticDataResponse.json();

    return {
      ok: true,
      data: fallbackStaticDataResponseJSON
    };

  } catch (err) {
    return {
      ok: false,
      errorObject: err,
      customErrorMessage: `An error occurred during fetching fallback static data from fo-app CDN URL: ${cdnUrl(url)}.` +
        "Or the response is not in the correct format and it's not possible to parse it to a proper JSON object." +
        'Check the URL or the returned data in the response',
      errorCode: 500
    };
  }
}

// gets channels availability for a specific products and specific location
// it calls one of these two fo-site POST endpoints based on format of location
// /gb/uk/mcd_multi_postcode_check/postcode/{postcode}/latitude/{latitude}/longitude/{longitude}/ajax.html
// /gb/uk/mcd_multi_postcode_check/latitude/{latitude}/longitude/{longitude}/ajax.html
// IDs of products are POST parameter
//
// it's used e. g. for showing channels availability for the specific location for products on listing page
// (if product can be delivered to the specific location or if it can be collected in a store nearby the specific location)
export async function fetchProductsChannelAvailabilities(productIds, location) {
  const TIMEOUT = UISettings.get('component.mcd.postcodechecker.timeout') || 60000;

  const data = {
    productIds
  };

  let url;
  if (!location.postcode) {
    url = prepareXhrUrl(PRODUCT_AVAILABILITIES_CHECK_URL_NO_POSTCODE, location);
  } else {
    url = prepareXhrUrl(PRODUCT_AVAILABILITIES_CHECK_URL, location);
  }
  const channelsAvailabilitiesResponse = await promiseWithTimeout(
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    }),
    TIMEOUT
  );

  const channelsAvailabilities = await channelsAvailabilitiesResponse.json();

  return channelsAvailabilities;

}

function prepareXhrUrl(url, location) {
  const decimals = 1000000; // 6 decimals because also Brain stores 6 decimals.

  let xhrUrl = url.replace(':postcode', location.postcode.replace(' ', ''));
  if (location.coords && location.coords.length === 2) {
    xhrUrl = xhrUrl
      .replace(':latitude', String(Math.round(location.coords[0] * decimals) / decimals))
      .replace(':longitude', String(Math.round(location.coords[1] * decimals) / decimals));
  }

  return xhrUrl;
}

// adds product to basket
// it calls POST fo-site endpoint /api/cart/addProduct
// POST parameter has format {fupid: {fupid}, quantity: {quantity}}

// it's used e. g. for adding products from Compare Popup to basket
export async function addToBasket({ fupid, quantity }) {

  try {
    const addToBasketResponse = await promiseWithTimeout(
      fetch(
        Configuration.get('api.listingPage.addToBasket'),
        {
          method: 'POST',
          credentials: 'same-origin',
          body: JSON.stringify({ fupid, quantity })
        }
      ), ADD_TO_BASKET_TIMEOUT
    );

    const responseJSON = await addToBasketResponse.json();

    if (addToBasketResponse.status !== 200 && responseJSON.popupHeaderLabel && responseJSON.popupMessage) {
      return {
        ok: false,
        customErrorMessage: `The product with id ${fupid} wasn't added to basket because it's incompatible with the current 
        products in basket`,
        errorMessageForGUI: responseJSON.popupMessage,
        errorCode: 500
      };
    }

    if (responseJSON.basketConfirmationUrl && responseJSON.redirectUrl) {
      return {
        ok: true,
        ...responseJSON
      };
    }

    return {
      ok: false,
      customErrorMessage: `The add to basket POST request ${Configuration.get('api.listingPage.addToBasket')} for adding 
      product with id ${fupid} to basket returned unknown response: ${responseJSON} 
      and status: ${addToBasketResponse.status}`,
      errorMessageForGUI: 'There has been a problem adding this item to your basket. Please try again.', // TODO: add to translations
      errorCode: 500
    };
  } catch (err) {
    return {
      ok: false,
      customErrorMessage: `A following error occurred during calling add to basket POST request 
      ${Configuration.get('api.listingPage.addToBasket')} for adding product with id ${fupid} to basket . 
      Error message: ${err.message} 
      Stack: ${err.stack}`,
      errorMessageForGUI: 'There has been a problem adding this item to your basket. Please try again.', // TODO: add to translations
      errorCode: 500
    };
  }
}

// This function is used only on client after search in header search input
// it calls Search API and depending on response, it returns URL to redirect after the search
export async function fetchSearchListingPageURLBasedOnSearchQuery(searchQuery, useFallbackSearchLabsURL) {
  // default URL for case of error or if searchLabs response doesn't contain redirect property
  const defaultURL = `${window.location.origin}/${resolveByCountry('gbuk', 'ieen')}/search-keywords/xx_xx_xx_xx_xx/` +
    `${encodeURI(searchQuery)}/xx-criteria.html`;

  const [customerSegment, config] = getURLParametersForSearchLabsABTest();

  try {
    const searchLabsRequestParams = {
      do: 'json',
      sp_cs: 'UTF-8',
      q: searchQuery,
      ...(customerSegment && { customer_segment: customerSegment }),
      ...(config && { config: config })
    };
    let searchLabsAPIURL = `${Configuration.get('api.listingPage.searchAndFilter')}?${stringify(searchLabsRequestParams)}`;
    searchLabsAPIURL = fallbackSearchLabsURLIfNeeded(searchLabsAPIURL, useFallbackSearchLabsURL);

    let searchLabsAPIResponse = await fetch(searchLabsAPIURL);

    if (searchLabsAPIResponse.ok) {
      const searchLabsAPIData = await searchLabsAPIResponse.json();

      if (searchLabsAPIData?.general?.redirect) {
        return searchLabsAPIData.general.redirect;
      }
      // searchLabs URL doesn't contain redirect URL so we use defaultURL
      return defaultURL;
    }
    if (searchLabsAPIURL.contains(SEARCHLABS_DOMAIN_CDN_PART)) {
      searchLabsAPIURL = removeSearchLabsCDNDomainPart(searchLabsAPIURL);
      searchLabsAPIResponse = await fetch(searchLabsAPIURL);
      if (searchLabsAPIResponse.ok) {
        const searchLabsAPIData = await searchLabsAPIResponse.json();

        if (searchLabsAPIData?.general?.redirect) {
          return searchLabsAPIData.general.redirect;
        }
        // searchLabs URL doesn't contain redirect URL so we use defaultURL
        return defaultURL;
      }
    }

    log.error(`Fetching data from SearchLabs from URL ${searchLabsAPIURL} finished with HTTP error code: 
        ${searchLabsAPIResponse.status}  And the error message: ${searchLabsAPIResponse.statusText} 
        Check if SearchLabs API working correctly`);

    // call to searchLabs failed so we return defaultURL
    return defaultURL;
  } catch (err) {
    log.error(`Message: ${err.message} Stack ${err.stack}`);
    // an error occurred so we return defaultURL
    return defaultURL;
  }
}

function promiseWithTimeout(promise, timeoutMs) {
  const timeoutPromise = new Promise((resolve, reject) => {
    const id = setTimeout(() => {
      clearTimeout(id);
      reject(new Error('Connection timed out'));
    }, timeoutMs);
  });

  return Promise.race([
    promise,
    timeoutPromise
  ]);
}

function createFetchOptionsWithHttpProxy() {
  // create http_proxy if request is triggered from node server
  // eslint-disable-next-line global-require
  const HttpsProxyAgent = require('https-proxy-agent');
  const proxy = (process.env.http_proxy.indexOf('http://') !== 0 ? 'http://' : '') + process.env.http_proxy;
  const agent = new HttpsProxyAgent(proxy);

  return { agent };
}

// get searchLabs URL request parameters for SearchLabs AB test
// the customer segment is not related to segment of the listing page in this function,
// it's an internal variable of SearchLabs
function getURLParametersForSearchLabsABTest() {
  let customerSegment;
  let config;

  // eslint-disable-next-line camelcase
  if (typeof window !== 'undefined' && typeof window?.Search_VisitorSegmentId === 'string') {
    // eslint-disable-next-line camelcase
    customerSegment = window?.Search_VisitorSegmentId;
    if (customerSegment.startsWith(CUSTOMER_SEGMENT_CONFIG_PREFIX)) {
      config = customerSegment.replace(CUSTOMER_SEGMENT_CONFIG_PREFIX, '');
    }
  }

  return [customerSegment, config];
}

// boolean values for pages and filters values in SearchLabs response are strings ( e. g. 'true', 'false')
// so we convert them to real boolean using this function
// TODO: discuss with findability team to change this values to bool type in JSON response of Search API
function convertStringBooleanValuesToRealBooleanInSearchLabsAPIData(searchLabsAPIData) {
  if (searchLabsAPIData.pagination && searchLabsAPIData.pagination.pages) {
    searchLabsAPIData.pagination.pages =
      searchLabsAPIData.pagination.pages.map(
        (page) => ({
          ...page,
          selected: page.selected === 'true' // mapping values of 'selected' prop from string to boolean
        })
      );
  }


  if (Array.isArray(searchLabsAPIData.facets)) {
    searchLabsAPIData.facets = searchLabsAPIData.facets.map(
      (filter) => ({ ...filter,
        values: filter.values.map(
          (value) => ({ ...value, selected: value.selected === 'true' })
        )
      })
    );
  }

  return searchLabsAPIData;
}
